import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Brain, CalendarClock, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Clock, Copy, Eye, FilePlus2,
  FileText, Flag, FolderInput, FolderOpen, GraduationCap, ListChecks, Loader2, Monitor, MoreHorizontal, PanelRight, Pause, Pencil,
  Plug, RotateCcw, Send, ShieldQuestion, Sparkles, Square, SquareCheckBig, ThumbsDown, ThumbsUp, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../../components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import BotAvatar, { botState } from '../../components/bots/BotAvatar';
import BotName from '../../components/bots/BotName';
import ProjectList from '../../components/bots/ProjectList';
import ComposerBotPicker from '../../components/bots/ComposerBotPicker';
import { ProjectDot, ProjectTile } from '../../components/layout/SidebarParts';
import { Popover, PopoverAnchor, PopoverContent } from '../../components/ui/popover';
import BotFormModal from '../../components/bots/BotFormModal';
import ApprovalCard from '../../components/bots/ApprovalCard';
import BotConnections from '../../components/bots/BotConnections';
import LearningPanel, { learnedText } from '../../components/bots/LearningPanel';
import SchedulesPanel from '../../components/bots/SchedulesPanel';
import SavedLogins from '../../components/bots/SavedLogins';
import {
  PanelEmpty, PanelIntro, PanelList, PanelRow, PanelSection, PanelToggle, RemoveButton, panelField,
} from '../../components/bots/panel/PanelKit';
import { DesktopUpsell } from '../../components/bots/DesktopUpsell';
import ComputerView, { useComputerCollapsed } from '../../components/bots/ComputerView';
import WorkspaceFiles from '../../components/bots/WorkspaceFiles';
import BotReply from '../../components/bots/BotReply';
import DictateButton from '../../components/bots/DictateButton';
import SpeakButton from '../../components/bots/SpeakButton';
import { AttachButton, MessageFiles, PendingFiles, useAttachments } from '../../components/bots/ChatFiles';
import { MESSAGE_MAX, isTooLong, pasteAsFile, pastedTextFile } from '../../lib/chatMessage';
import { clipboardFiles, isFilePaste } from '../../lib/clipboardFiles';
import { RUNNING, WORKSPACE_TOOLS, activityLabel } from '../../components/bots/botActivity';
import ModelSelect, { modelLabel } from '../../components/bots/ModelSelect';
import { botDisplayName, homePlaceId, useBotLabel } from '../../components/bots/botDisplay';
import { lastBotIn } from '../../components/bots/defaultBot';
import { chatPath, newChatPath } from '../../components/bots/chatLinks';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../contexts/AuthContext';
import {
  useBotChatQuery, useBotLearningQuery, useBotMemoryQuery, useBotModelsQuery, useBotQuery,
  useMyBotsQuery, useProjectBotsPausedQuery, useWorkspaceQuery,
} from '../../hooks/queries';
import {
  useAddMemory, useCancelRun, useCreateBotChat, useDeleteBotChat, useForgetMemory, useRetryBotChat,
  useRateAnswer, useReportAnswer, useSaveAnswerAsDocument, useSendBotMessage, useSetProjectBotsPaused, useUndoMemoryEvent, useUpdateBotChat,
  useCancelWaitingMessage, useDecideApproval, useDeleteBot, useMoveBotChat,
} from '../../hooks/mutations';
import useBotRunStreams from '../../hooks/useBotRunStreams';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useTask } from '../../contexts/TaskContext';
import { useDateTime } from '../../contexts/DateTimeContext';
import { botsApi } from '../../services/api';
import { botKeys } from '../../lib/queryKeys';
import { saveFile } from '../../native/files';
import { takeShareDraft } from '../../native/shareDraft';
import { useViewing } from '../../lib/viewing';
import Segmented from '../../components/ui/segmented';

const ACTIVE = RUNNING;
const SUGGESTIONS = ['today', 'summary', 'overdue', 'week', 'plan_doc', 'subtasks'];
const WIDE = '(min-width: 1024px)';

const stored = (key, fallback) => ({
  get() {
    try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
  },
  set(value) {
    try { localStorage.setItem(key, value); } catch { /* private mode */ }
  },
});
// What the right-hand panel shows on wide screens: the project, the computer or nothing.
const storedSide = stored('bb.bot.side', 'project');

const useWide = (query = WIDE) => {
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    setWide(mql.matches);
    const onChange = (e) => setWide(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return wide;
};

/** The bot's side of the conversation, like Rakazo and OpenMausBot: its answer in a bubble
 * (BotReply bubble) and no face next to it, the bot is in the chat's header. */
const BotTurn = ({ children }) => <div className="group min-w-0 text-foreground">{children}</div>;

// A time between messages, like Rakazo and OpenMausBot: before the first one and after a pause.
const TIME_GAP_MS = 30 * 60 * 1000;
// A chat opens on its latest items; older ones are drawn when the person scrolls up to them.
// Drawing a long chat at once (every answer's Markdown) took most of a second on phones.
const CHAT_PAGE = 20;
const itemAt = (item) => (item.type === 'message' ? item.m.created_at : item.type === 'reply' ? item.r.created_at : null);

const CopyButton = ({ text, t }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };
  return (
    <button type="button" onClick={copy} title={t(copied ? 'bots.chat.copied' : 'bots.chat.copy')}
      className="p-1 rounded hover:bg-accent hover:text-foreground">
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

const MEMORY_LABEL = { add: 'remembered', replace: 'changed', remove: 'forgot' };

const PILL = 'inline-flex items-center gap-2 max-w-[90%] text-xs text-muted-foreground rounded-full border border-border bg-card px-3 py-1';

/** "Remembered: ..." with Undo: one thing the bot took from this conversation. */
const MemoryEventRow = ({ event, t, onUndo }) => (
  <>
    <Brain className="w-3.5 h-3.5 text-amber-500 shrink-0" />
    <span className={`truncate ${event.undone_at ? 'line-through' : ''}`}>
      {t(`bots.memory.${MEMORY_LABEL[event.action]}`, { content: event.content })}
    </span>
    {event.undone_at ? (
      <span className="shrink-0">{t('bots.memory.undone')}</span>
    ) : (
      <button type="button" onClick={() => onUndo(event.id)} className="shrink-0 font-medium text-foreground hover:underline">
        {t('bots.memory.undo')}
      </button>
    )}
  </>
);

/** "Learned the skill ..." or "Suggested ..." with a link to the learning panel. */
const LearnedRow = ({ item, t, onOpen }) => (
  <>
    <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
    <span className={`truncate ${item.status === 'undone' || item.status === 'rejected' ? 'line-through' : ''}`}>
      {learnedText(item, t)}
    </span>
    <button type="button" onClick={onOpen} className="shrink-0 font-medium text-foreground hover:underline">
      {t(item.status === 'pending' ? 'bots.learning.review' : 'bots.learning.open')}
    </button>
  </>
);

const NoteRow = ({ note, t, onUndo, onOpen }) => (note.type === 'memory'
  ? <MemoryEventRow event={note.e} t={t} onUndo={onUndo} />
  : <LearnedRow item={note.l} t={t} onOpen={onOpen} />);

/** What the bot learned after an answer, close under it (on a computer as far as the answer's
 *  actions shown on hover need). One thing is one line; more fold
 *  into a single line that opens to the list (Hermes' one-line review summary, ChatGPT's
 *  "Memory updated"), so learning never pushes the conversation apart. */
const LearnedNotes = ({ notes, t, onUndo, onOpen }) => {
  const [open, setOpen] = useState(false);
  if (notes.length === 1) {
    return (
      <div className="flex justify-center !mt-2 md:!mt-6" data-testid={notes[0].type === 'memory' ? 'memory-event' : 'learned-event'}>
        <span className={PILL}><NoteRow note={notes[0]} t={t} onUndo={onUndo} onOpen={onOpen} /></span>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center !mt-2 md:!mt-6" data-testid="learned-notes">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="learned-notes-toggle"
        className={`${PILL} hover:text-foreground`}>
        <Brain className="w-3.5 h-3.5 text-amber-500 shrink-0" />
        <span className="truncate">{t('bots.memory.learned_count', { count: notes.length })}</span>
        <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <ol className="mt-1.5 max-w-[90%] min-w-0 border-l border-border pl-3 space-y-1 text-xs text-muted-foreground">
          {notes.map((n) => (
            <li key={n.type === 'memory' ? n.e.id : n.l.id} className="flex items-center gap-2 min-w-0"
              data-testid={n.type === 'memory' ? 'memory-event' : 'learned-event'}>
              <NoteRow note={n} t={t} onUndo={onUndo} onOpen={onOpen} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

/** After a thumbs down: what was wrong, so the bot learns from it. Also the note
 *  of a report (placeholder and button given). */
const FeedbackNote = ({ onSend, onClose, busy, t, placeholder, sendLabel }) => {
  const [note, setNote] = useState('');
  return (
    <div className="mt-1.5 flex items-center gap-1.5 max-w-md" data-testid="feedback-note">
      <input autoFocus value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000}
        onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) onSend(note.trim()); if (e.key === 'Escape') onClose(); }}
        placeholder={placeholder || t('bots.feedback.note_placeholder')}
        className="flex-1 min-w-0 text-xs rounded-md border border-border bg-transparent px-2 py-1.5 focus:outline-none focus:border-amber-500/60" />
      <Button size="sm" className="h-7 px-2 text-xs bg-amber-500 text-black hover:bg-amber-400" disabled={!note.trim() || busy}
        onClick={() => onSend(note.trim())}>{sendLabel || t('bots.feedback.send')}</Button>
      <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" aria-label={t('common.close')}>
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

/** What bots remember in this project: shared facts, and the viewer's own part. */
const MemoryPanel = ({ projectId, projectName, t }) => {
  const { data } = useBotMemoryQuery(projectId);
  const add = useAddMemory();
  const forget = useForgetMemory();
  const [draft, setDraft] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content) return;
    add.mutate({ projectId, content }, {
      onSuccess: () => setDraft(''),
      onError: (err) => {
        const detail = err?.response?.data?.detail;
        toast.error(detail?.error === 'memory_secret' ? t('bots.memory.secret_refused')
          : (typeof detail === 'string' && detail) || t('bots.memory.add_failed'));
      },
    });
  };
  const list = (rows) => (rows.length === 0 ? (
    <p className="text-xs text-muted-foreground">{t('bots.memory.empty')}</p>
  ) : (
    <PanelList>
      {rows.map((e) => (
        <PanelRow key={e.id} wrap title={e.content} titleAttr={e.bot_name || undefined}
          trailing={<RemoveButton onClick={() => forget.mutate(e.id)} title={t('bots.memory.forget')} />} />
      ))}
    </PanelList>
  ));
  return (
    <div className="space-y-5" data-testid="bot-memory">
      <PanelIntro>{t('bots.memory.hint', { project: projectName })}</PanelIntro>
      <PanelSection title={t('bots.memory.project_title')} count={(data?.project || []).length}>
        {list(data?.project || [])}
      </PanelSection>
      <PanelSection title={t('bots.memory.user_title')} count={(data?.user || []).length}>
        {list(data?.user || [])}
        <form onSubmit={submit}>
          <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={500}
            placeholder={t('bots.memory.add_placeholder')} className={panelField} />
        </form>
      </PanelSection>
      {(data?.profile || []).length > 0 && (
        <PanelSection title={t('bots.memory.profile_title')} count={data.profile.length}>{list(data.profile)}</PanelSection>
      )}
    </div>
  );
};

/** Tasks handed to the bot in this project, with what it is doing on each. */
const taskStatusLabel = (t, task, live) => {
  const status = task.run?.status;
  if (RUNNING.has(status) && live[task.run.id]) return activityLabel(t, live[task.run.id]);
  if (status === 'done' && task.bot_review) return t('bots.control.ready_for_review');
  return status ? t(`bots.chat.status_${status}`) : null;
};

const taskIcon = (task, status, active) => (
  status === 'done' && task.bot_review ? <Eye className="w-4 h-4 text-sky-500" />
    : status === 'done' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      : status === 'failed' ? <AlertTriangle className="w-4 h-4 text-red-500" />
        : status === 'waiting_approval' ? <ShieldQuestion className="w-4 h-4 text-amber-500" />
          : active ? <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
            : <SquareCheckBig className="w-4 h-4" />
);

const ProjectTasks = ({ tasks, live, t, onOpenTask, onStop }) => (
  <PanelSection title={t('bots.chat.project_tasks')} count={tasks.length}>
    {tasks.length === 0 ? (
      <PanelEmpty icon={ListChecks}>{t('bots.chat.project_tasks_empty')}</PanelEmpty>
    ) : (
      <PanelList>
        {tasks.map((task) => {
          const status = task.run?.status;
          const active = ACTIVE.has(status);
          return (
            <PanelRow key={task.id} icon={taskIcon(task, status, active)} title={task.title}
              subtitle={taskStatusLabel(t, task, live)} onClick={() => onOpenTask(task.id)}
              trailing={active && (
                <button type="button" onClick={() => onStop(task.run.id)} title={t('bots.control.stop')}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-accent">
                  <Square className="w-3 h-3" />
                </button>
              )} />
          );
        })}
      </PanelList>
    )}
  </PanelSection>
);

/** The most credits the project's bots may use a month (empty: no limit besides the plan). */
const TABS = [
  { key: 'tasks', icon: ListChecks },
  { key: 'memory', icon: Brain },
  { key: 'learning', icon: GraduationCap },
  { key: 'schedules', icon: CalendarClock },
  { key: 'apps', icon: Plug },
];
const storedTab = stored('bb.bot.tab', 'tasks');

/** The project around the chat, in tabs: tasks, memory, learning, schedules, apps. */
const ProjectPanel = ({ t, badges, children, onClose, tabRequest }) => {
  const [tab, setTab] = useState(() => (TABS.some((x) => x.key === storedTab.get()) ? storedTab.get() : 'tasks'));
  const pick = (key) => { setTab(key); storedTab.set(key); };
  useEffect(() => { if (tabRequest) setTab(tabRequest.key); }, [tabRequest]);
  return (
    <div className="flex flex-col h-full min-h-0" data-testid="bot-project-panel">
      <div className="flex items-center gap-1.5 px-3 pt-3 pb-2">
        <Segmented fill className="flex-1 min-w-0" value={tab} onChange={pick}
          items={TABS.map(({ key }) => ({ key, label: t(`bots.panel.${key}`), badge: badges[key], testId: `bot-tab-${key}` }))} />
        {onClose && (
          <button type="button" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-4 pt-2 pb-6 space-y-5">{children(tab)}</div>
    </div>
  );
};

/**
 * A bot's chat, in the Chats page (pages/app/ChatsPage.jsx) or in a project's chat panel
 * (embedded). The host says which chat it is (embedParams: chat, an id or 'new', and project,
 * where a new chat starts) and moves on when the chat changes: onEmbedParams gets the next
 * { project, chat }, onEmbedBot the bot picked in the composer (a new chat with it). In the
 * Chats page the chat draws its own header (the bot, the chat's title, "Bot · Project", a live
 * Computer chip and everything else in one "..." menu; onBack: the arrow back to the history
 * on phones); in a panel the panel draws it (embedToolbar gets the chat's title and this
 * chat's computer and details buttons, so nothing is lost there). wideAt: from which screen
 * width the computer and the project's details open beside the chat (the Chats page keeps its
 * history next to it, so it asks for more room); narrower, they open above it or in a sheet.
 */
const BotPage = ({
  embedded = false, botId, embedParams, onEmbedParams, onEmbedBot, embedToolbar, onBack, onDeleted, wideAt = WIDE,
} = {}) => {
  const botLabel = useBotLabel();
  const { t, i18n } = useTranslation();
  const { formatDate, formatTime } = useDateTime();
  const { openTaskDetail } = useTask();
  const params = useMemo(() => ({ get: (key) => embedParams?.[key] ?? null }), [embedParams]);
  const { data: detail, isLoading, isError } = useBotQuery(botId);
  const { data: mine } = useMyBotsQuery();
  const createChat = useCreateBotChat();
  const moveChat = useMoveBotChat();
  const { user } = useAuth();
  const deleteChat = useDeleteBotChat();
  const deleteBot = useDeleteBot();  // undoes a bot the main chat made (create_bot)
  const cancelWaiting = useCancelWaitingMessage();
  const send = useSendBotMessage();
  const undoMemory = useUndoMemoryEvent();
  const cancelRun = useCancelRun();
  // The run the person pressed Stop on: the button says so at once, until the run has ended
  // (a second or two: backend agents/runtime._watched ends even a command under way).
  const [stopping, setStopping] = useState(null);
  const decideCard = useDecideApproval();
  const updateChat = useUpdateBotChat();
  const [newChatModel, setNewChatModel] = useState(undefined);  // picked before the chat exists
  const retryChat = useRetryBotChat();
  const saveDoc = useSaveAnswerAsDocument();
  const rate = useRateAnswer();
  const report = useReportAnswer();
  const [reportFor, setReportFor] = useState(null);  // the answer being reported
  const navigate = useNavigate();
  const wideScreen = useWide(wideAt);
  const wide = wideScreen && !embedded;
  const isMobile = useIsMobile();
  const [projectOpen, setProjectOpen] = useState(false);
  const [input, setInput] = useState('');
  const [editing, setEditing] = useState(false);
  const [renaming, setRenaming] = useState(null);  // the title being typed in the header
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [tabRequest, setTabRequest] = useState(null);
  // A card asks to open a tab of the panel (ConnectCard: the bot's Apps, to enter a key).
  const openTabRef = useRef(null);
  useEffect(() => {
    const open = (e) => openTabRef.current?.(e.detail);
    window.addEventListener('bb:open-bot-tab', open);
    return () => window.removeEventListener('bb:open-bot-tab', open);
  }, []);
  const [noteFor, setNoteFor] = useState(null);  // the answer whose thumbs down gets a note
  const [side, setSideState] = useState(() => storedSide.get());
  // Above the chat on narrow screens: follows whether the computer is in use until the person toggles it.
  const [narrowComputer, setNarrowComputer] = useState(null);
  const [computerCollapsed, setComputerCollapsed] = useComputerCollapsed();
  const [dragging, setDragging] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const moveAfterMenu = useRef(false);  // "Move to project" opens the picker once the menu has closed
  // A page in another tab opens once the menu has closed: on a phone the chat's screen is kept,
  // hidden, and a menu still closing in it stayed on top of the next page.
  const goAfterMenu = useRef(null);
  const setSide = (value) => { setSideState(value); storedSide.set(value); };

  const bot = detail?.bot;
  // BusyBee's main chat is in no project: it hands conversations to bots in projects instead.
  const isQueen = !!bot?.queen;
  const projects = useMemo(() => detail?.projects || [], [detail]);
  const teamId = bot?.owner_type === 'team' ? bot.owner_id : null;
  const { data: catalog } = useBotModelsQuery({ bot_id: botId }, { enabled: !!bot });
  const models = catalog?.models || [];
  // "Inherit" names the model the run really uses: in a chat, the bot's own model or
  // else this person's default; for the bot's own model, this person's default.
  const inheritFor = (effective) => t('bots.inherit_with', {
    model: modelLabel(models, effective?.model_id) || t('bots.source_default'),
  });
  // In a chat "inherit" is the bot's own model (Settings > Bots); a chat's choice is its own.
  const chatInheritLabel = t('bots.chat_inherit_with', {
    model: modelLabel(models, catalog?.bot_effective?.model_id) || t('bots.source_default'),
  });
  const botInheritLabel = inheritFor(catalog?.inherited);
  const scope = mine?.scopes?.find((s) => bot && s.type === bot.owner_type && s.id === bot.owner_id);
  const name = bot ? botDisplayName(bot, t) : '';
  const label = bot ? botLabel(bot) : '';  // with the team when another bot reads the same

  const known = (id) => !!id && projects.some((p) => p.id === id);
  const projectParam = params.get('project');
  const chatParam = params.get('chat');
  // The open chat: the one the host names, or none for a new chat.
  const chatId = chatParam && chatParam !== 'new' ? chatParam : null;
  const { data: chatData } = useBotChatQuery(chatId);
  const current = chatData?.chat?.id === chatId ? chatData : null;
  // A chat with something new from the bot is read once it is opened: its unread dot leaves the lists.
  const queryClient = useQueryClient();
  const readNow = current?.chat?.unread ? current.chat.id : null;
  useEffect(() => {
    if (!readNow) return;
    ['chats', 'project-chats', 'chat-counts', 'mine'].forEach((k) => queryClient.invalidateQueries({ queryKey: [...botKeys.all, k] }));
  }, [readNow, queryClient]);
  useViewing(chatId ? `chat:${chatId}` : null);  // no ping about the chat in view
  // A task's thread: its bot notifications are about the task (backend agents/runtime._subject).
  useViewing(current?.chat?.task_id ? `task:${current.chat.task_id}` : null);
  // The project is always the open chat's own, which may be none (backend agents/places.py);
  // a new chat starts in the project the host names (a project's chat panel, a new chat from a
  // project), else in none, as a new chat in ChatGPT or Claude starts outside projects.
  // BusyBee's main chat is never in a project.
  const openChat = current?.chat || null;
  const projectId = (openChat ? openChat.project_id : known(projectParam) && !isQueen ? projectParam : null) || null;
  const project = projectId ? (projects.find((p) => p.id === projectId) || openChat?.project || null) : null;
  const ownChat = !!openChat && openChat.user_id === user?.id;  // a teammate's task thread is read only
  // The computer: the project's, or outside projects the person's own (per payer).
  const placeId = projectId || homePlaceId(bot, user?.id);
  const { data: workspace } = useWorkspaceQuery(placeId);
  const { data: learning } = useBotLearningQuery(projectId);
  const attachments = useAttachments(botId, projectId);
  const { data: pausedState } = useProjectBotsPausedQuery(projectId);
  const setPaused = useSetProjectBotsPaused();
  const paused = pausedState?.service_paused ? 'service' : pausedState?.paused ? 'project'
    : pausedState?.over_budget ? 'budget' : null;

  const projectTasks = useMemo(
    () => (detail?.tasks || []).filter((task) => task.project_id === projectId), [detail, projectId]);
  const runs = useMemo(() => current?.runs || [], [current]);
  const runsById = useMemo(() => Object.fromEntries(runs.map((r) => [r.id, r])), [runs]);
  const live = useBotRunStreams(useMemo(
    () => [...runs, ...projectTasks.map((task) => task.run).filter(Boolean)], [runs, projectTasks]));

  // The conversation in the server's order: every answer is a stored message from the
  // moment its run is queued (backend agents/conversation.py). Runs from before that
  // without a message still show where they started; what the bot remembered or learned
  // shows under the answer it came from.
  const timeline = useMemo(() => {
    const messages = current?.messages || [];
    const replied = new Set(messages.map((m) => m.run_id).filter(Boolean));
    const items = messages.map((m, i) => ({ type: 'message', at: m.created_at, i, m }));
    runs.forEach((r) => {
      if (r.status !== 'done' && !replied.has(r.id)) items.push({ type: 'reply', at: r.created_at, i: -1, r });
    });
    (current?.memory_events || []).forEach((e) => items.push({ type: 'memory', at: e.created_at, i: 1e6, e }));
    (current?.learned || []).forEach((l) => items.push({ type: 'learned', at: l.created_at, i: 1e6, l }));
    const time = (x) => new Date(x.at).getTime() || 0;
    // What was learned after one answer shows as one item (agents.runtime saves it all in one review).
    return items.sort((a, b) => time(a) - time(b) || a.i - b.i).reduce((out, item) => {
      const prev = out[out.length - 1];
      if (item.type !== 'memory' && item.type !== 'learned') out.push(item);
      else if (prev?.type === 'notes') prev.notes.push(item);
      else out.push({ type: 'notes', notes: [item] });
      return out;
    }, []);
  }, [current, runs]);

  const openRun = runs.find((r) => ACTIVE.has(r.status) || r.status === 'waiting_approval');
  const busy = !!openRun;
  const chatApprovals = current?.approvals || [];
  // A question card is open: what the person types in the composer answers it (like Claude).
  const openQuestion = chatApprovals.find((a) => a.status === 'pending' && a.card?.kind === 'question' && a.card.allow_other);
  const projectApprovals = (detail?.approvals || []).filter((a) => a.project_id === projectId && a.task_id);
  // What the bot does in this chat, for the computer's status: its screen alone can't
  // tell "waiting for you" or "answered" from idle.
  const lastRun = runs.reduce((a, r) => (!a || new Date(r.created_at) > new Date(a.created_at) ? r : a), null);
  const lastStatus = (lastRun && live[lastRun.id]?.status) || lastRun?.status;
  const activity = chatApprovals.some((a) => a.status === 'pending') || openRun?.status === 'waiting_approval' ? 'waiting'
    : openRun ? 'working'
      : lastStatus === 'failed' ? 'failed'
        : lastStatus === 'done' ? ((live[lastRun.id]?.stoppedBy || lastRun.stopped_by) ? 'stopped' : 'answered')
          : undefined;
  const liveText = Object.values(live).map((l) => l.text.length + (l.tool || '') + (l.steps?.length || 0)).join();

  // Like Grok Bot: the computer comes up next to the chat when the bot starts using it.
  // Once per run, so closing the panel while the bot works keeps it closed.
  const computerRun = Object.entries(live).find(([, l]) => l.action && WORKSPACE_TOOLS.has(l.action.tool)
    && ACTIVE.has(l.status))?.[0];
  useEffect(() => {
    if (computerRun && wide) { setSideState('computer'); storedSide.set('computer'); }
  }, [computerRun, wide]);

  // The chat is drawn once the bot is loaded too (a loader stands in before): when its messages
  // came first, the chat opened at its oldest message instead of its latest.
  const drawnReady = !!bot && !isLoading;
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [timeline.length, liveText, chatId, drawnReady]);
  // How many of the latest items are drawn (CHAT_PAGE more each time the top comes into view),
  // keeping what the person looks at in place while older ones are added above it.
  const scrollRef = useRef(null);
  const topRef = useRef(null);
  const heightBefore = useRef(null);
  const [drawn, setDrawn] = useState({ chat: chatId, n: CHAT_PAGE });
  const drawnCount = drawn.chat === chatId ? drawn.n : CHAT_PAGE;
  const firstDrawn = Math.max(0, timeline.length - drawnCount);
  useEffect(() => {
    const top = topRef.current;
    if (!top || !firstDrawn) return undefined;
    const seen = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      heightBefore.current = scrollRef.current?.scrollHeight ?? null;
      setDrawn({ chat: chatId, n: drawnCount + CHAT_PAGE });
    }, { root: scrollRef.current, rootMargin: '300px 0px 0px 0px' });
    seen.observe(top);
    return () => seen.disconnect();
  }, [chatId, drawnCount, firstDrawn, drawnReady]);
  useLayoutEffect(() => {
    const box = scrollRef.current;
    if (box && heightBefore.current != null) box.scrollTop += box.scrollHeight - heightBefore.current;
    heightBefore.current = null;
  }, [drawnCount]);
  // Not on phones: the keyboard would cover the chat as soon as it opens.
  useEffect(() => { setInput(''); if (!window.matchMedia('(max-width: 767px)').matches) inputRef.current?.focus(); }, [botId, projectId, chatId]);
  // Something shared to this bot from another app (native/shareDraft.js): in the new chat's box,
  // the person sends it. After the line above, which empties the box for a new chat.
  const addFiles = attachments.add;
  useEffect(() => {
    if (chatId || !botId) return undefined;
    const fill = () => {
      const draft = takeShareDraft(botId);
      if (!draft) return;
      if (draft.text) {
        setInput(draft.text);
        // As typing would: the box grows to show the whole text (up to its limit).
        requestAnimationFrame(() => {
          const el = inputRef.current;
          if (!el) return;
          el.style.height = 'auto';
          el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
          el.style.overflowY = el.scrollHeight > 200 ? 'auto' : 'hidden';
        });
      }
      if (draft.files?.length) addFiles(draft.files);
    };
    fill();
    window.addEventListener('bb:share-draft', fill);
    return () => window.removeEventListener('bb:share-draft', fill);
  }, [botId, projectId, chatId, addFiles]);

  const go = (next) => {
    const clean = Object.fromEntries(Object.entries(next).filter(([, v]) => v));
    onEmbedParams?.(clean);
  };
  // Another bot for this project: a new chat with it. In a project it is the bot used there
  // last from now on (components/bots/defaultBot.js), so the next new chat starts with it.
  const pickBot = (id) => {
    if (projectId) lastBotIn.set(projectId, id);
    onEmbedBot?.(id);
  };
  // Another project: a new chat there (its earlier chats stay in the history).
  const selectProject = (id) => go({ project: id, chat: 'new' });
  const selectChat = (id) => go({ project: projectId, chat: id });
  // A chat in no project into a project (the bot's suggestion or the header's picker): it
  // keeps its history and the bot continues there (backend move_bot_chat).
  const moveToProject = (id) => moveChat.mutate({ chatId, projectId: id }, {
    onSuccess: () => go({ project: id, chat: chatId }),
    onError: (err) => toast.error(err?.response?.data?.detail || t('bots.chat.send_failed')),
  });

  const handleDelete = (id) => deleteChat.mutate(id, {
    onSuccess: () => {
      if (id !== chatId) return;
      if (onDeleted) onDeleted(); else go({ project: projectId, chat: 'new' });
    },
  });
  const saveTitle = () => {
    const next = (renaming || '').trim();
    setRenaming(null);
    if (chatId && next && next !== (current?.chat?.title || '')) updateChat.mutate({ chatId, title: next });
  };

  const answerQuestion = () => {
    const answer = input.trim();
    if (!answer || decideCard.isPending) return;
    decideCard.mutate({ approvalId: openQuestion.id, approve: true, answer: openQuestion.card.multiple ? [answer] : answer }, {
      onSuccess: () => setInput(''),
      onError: (err) => toast.error(err?.response?.data?.detail || t('bots.chat.send_failed')),
    });
  };

  const handleSend = async (text) => {
    if (openQuestion && text === undefined) { answerQuestion(); return; }
    const content = (text ?? input).trim();
    const fileIds = text === undefined ? attachments.ids : [];
    // While the bot works the message waits for it (it reads it at its next step), as in Rakazo and OpenMausBot.
    if ((!content && !fileIds.length) || attachments.uploading || send.isPending || createChat.isPending) return;
    if (paused) { toast.error(t(`bots.paused.${paused}`)); return; }  // before a new chat is made for nothing
    try {
      let id = chatId;
      if (!id) {
        id = (await createChat.mutateAsync({ botId, projectId })).id;
      }
      await send.mutateAsync({ chatId: id, content, fileIds, modelId: id !== chatId ? newChatModel : undefined });
      if (projectId && !isQueen) lastBotIn.set(projectId, botId);  // the bot used here last
      setInput('');
      if (id !== chatId) setNewChatModel(undefined);
      if (fileIds.length) attachments.clear();
      if (inputRef.current) inputRef.current.style.height = 'auto';
      if (id !== chatId) go({ project: projectId, chat: id });
    } catch (err) {
      // Out of credits opens the upgrade dialog; no second message needed.
      if (err?.response?.status === 423) toast.error(t(`bots.paused.${err.response.data?.detail?.scope || 'project'}`));
      else if (isTooLong(err)) toast.error(t('bots.chat.too_long', { count: content.length, max: MESSAGE_MAX }));
      else if (err?.response?.status !== 402) toast.error(t('bots.chat.send_failed'));
    }
  };

  // Before the bot is there: on phones the app's top bar is hidden in a chat, so the way back stays.
  const bare = (body) => (
    <div className="h-full flex flex-col">
      {onBack && (
        <div className="flex items-center flex-shrink-0 h-[calc(3.5rem+env(safe-area-inset-top))] px-1" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <button type="button" onClick={onBack} aria-label={t('common.back')} title={t('common.back')}
            className="w-9 h-9 inline-flex items-center justify-center rounded-full text-foreground hover:bg-accent">
            <ChevronLeft className="w-6 h-6" />
          </button>
        </div>
      )}
      <div className="flex-1 flex items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">{body}</div>
    </div>
  );
  if (isError) return bare(<><AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />{t('bots.chat.not_found')}</>);
  if (isLoading || !bot) return bare(<Loader2 className="w-5 h-5 animate-spin" />);

  const hasComputer = !!workspace?.configured;
  const computerOn = workspace?.workspace?.status === 'running';
  const computerBusy = computerOn || workspace?.workspace?.status === 'creating' || !!computerRun;
  const computerShown = narrowComputer ?? computerBusy;
  const toggleNarrowComputer = () => setNarrowComputer(!computerShown);
  const badges = { tasks: projectApprovals.length, learning: learning?.proposals?.length || 0 };
  const panelContent = (tab) => {
    if (tab === 'memory') return <MemoryPanel projectId={projectId} projectName={project?.name} t={t} />;
    if (tab === 'learning') return <LearningPanel projectId={projectId} onTeach={teach} />;
    if (tab === 'schedules') return <SchedulesPanel botId={botId} projectId={projectId} onOpenChat={selectChat} />;
    if (tab === 'apps') {
      return (
        <>
          <BotConnections botId={botId} canEdit={!!scope?.can_edit} isTeam={bot.owner_type === 'team'}
            projectId={projectId} projectName={project?.name} />
          <SavedLogins projectId={projectId} />
        </>
      );
    }
    return (
      <>
        {projectApprovals.length > 0 && (
          <PanelSection title={t('bots.control.needs_ok')} count={projectApprovals.length} tone="attention">
            {projectApprovals.map((a) => <ApprovalCard key={a.id} approval={a} botName={label} compact />)}
          </PanelSection>
        )}
        <ProjectTasks tasks={projectTasks} live={live} t={t} onOpenTask={openTaskDetail}
          onStop={(runId) => cancelRun.mutate(runId)} />
        <PanelSection title={t('bots.panel.settings')}>
          <PanelToggle data-testid="bots-pause-switch" title={t('bots.paused.switch')}
            hint={t(pausedState?.paused ? 'bots.paused.switch_off_hint' : 'bots.paused.switch_on_hint')}
            checked={!pausedState?.paused} disabled={setPaused.isPending || !pausedState}
            onChange={(on) => setPaused.mutate({ projectId, paused: !on }, {
              onError: () => toast.error(t('bots.paused.no_permission')),
            })} />
          {isMobile && scope?.can_edit && (  // phones have no edit button in the header
            <PanelList data-testid="bot-edit-mobile">
              <PanelRow icon={<Pencil className="w-3.5 h-3.5" />} title={t('bots.edit_bot')}
                onClick={() => { setDetailsOpen(false); setEditing(true); }}
                trailing={<ChevronRight className="w-4 h-4 text-muted-foreground" />} />
            </PanelList>
          )}
        </PanelSection>
      </>
    );
  };
  const sending = send.isPending || createChat.isPending;
  const toggleProjectPanel = () => {
    if (!wide) { setDetailsOpen(true); return; }
    setSide(side === 'project' ? 'none' : 'project');
  };
  const iconButton = 'inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent';

  const overLimit = input.trim().length > MESSAGE_MAX;
  const canSend = (input.trim() || attachments.ids.length) && !attachments.uploading && !sending && !overLimit;
  const composer = (
    <div className="w-full rounded-2xl border border-input bg-popover focus-within:border-amber-500/60 transition-colors shadow-sm">
      <PendingFiles items={attachments.items} onRemove={attachments.remove} />
      <textarea
        ref={inputRef}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          const el = e.target;
          el.style.height = 'auto';
          el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
          el.style.overflowY = el.scrollHeight > 200 ? 'auto' : 'hidden';
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            handleSend();
          }
        }}
        onPaste={(e) => {
          // A screenshot or a file copied in the Finder / Explorer: attached, its name not typed in;
          // text that carries a picture of itself (Word, a web page) stays text (lib/clipboardFiles.js).
          if (isFilePaste(e)) {
            e.preventDefault();
            attachments.add(clipboardFiles(e));
            return;
          }
          const text = e.clipboardData?.getData('text') || '';
          if (pasteAsFile(text, input)) {  // a long paste goes in as a file (lib/chatMessage.js)
            e.preventDefault();
            attachments.add([pastedTextFile(text)]);
            toast.success(t('bots.chat.pasted_as_file'));
          }
        }}
        rows={1}
        placeholder={openQuestion ? t('bots.cards.answer_placeholder')
          : openRun && ACTIVE.has(openRun.status) ? t('bots.chat.placeholder_busy', { name })
            : isMobile && timeline.length ? t('bots.chat.placeholder_reply') : t('bots.chat.placeholder', { name })}
        className="block w-full resize-none bg-transparent max-md:text-base text-sm text-foreground placeholder:text-muted-foreground/70 px-5 pt-3.5 pb-1 focus:outline-none overflow-hidden"
        style={{ minHeight: '44px' }}
      />
      {input.trim().toLowerCase().startsWith('/learn') && (
        <div className="px-5 pb-1 flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400" data-testid="learn-hint">
          <Sparkles className="w-3 h-3" />{t('bots.learning.learn_hint')}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 px-2 pb-2">
        <span className="flex items-center min-w-0">
          <AttachButton onPick={attachments.add} disabled={attachments.items.length >= 5} />
          <DictateButton value={input} onChange={setInput} disabled={busy} />
          <ComposerBotPicker bot={bot} project={project} onPick={pickBot} disabled={busy} className="ml-1" />
          {models.length > 0 && !isMobile && (
            <ModelSelect models={models} value={chatId ? (current?.chat?.model_id ?? null) : (newChatModel ?? null)}
              inheritLabel={chatInheritLabel} heading={t('bots.chat_model_heading')} disabled={busy}
              onChange={(v) => (chatId ? updateChat.mutate({ chatId, model_id: v || '' }) : setNewChatModel(v))}
              className={`h-8 w-auto min-w-0 ${embedded ? 'max-w-[120px]' : 'max-w-[190px]'} gap-1 border-0 bg-transparent shadow-none px-2 text-xs text-muted-foreground hover:text-foreground focus:ring-0 [&>span]:truncate`} />
          )}
        </span>
      {busy && !input.trim() && !attachments.ids.length ? (  // with something typed, Send: it waits for the bot
        <button type="button" disabled={cancelRun.isPending}
          onClick={() => {
            setStopping(openRun.id);
            cancelRun.mutate(openRun.id, { onError: () => setStopping(null) });
          }}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-foreground text-background [@media(hover:hover)]:hover:opacity-80 disabled:opacity-30 transition-colors"
          aria-label={t(stopping === openRun.id ? 'bots.control.stopping' : 'bots.control.stop')}
          title={t(stopping === openRun.id ? 'bots.control.stopping' : 'bots.control.stop')} data-testid="bot-stop">
          {stopping === openRun.id
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Square className="w-3.5 h-3.5 fill-current" />}
        </button>
      ) : (
        <span className="flex items-center gap-2">
        {input.length > MESSAGE_MAX * 0.9 && (
          <span className={`text-[11px] tabular-nums ${overLimit ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}
            title={overLimit ? t('bots.chat.too_long', { count: input.trim().length, max: MESSAGE_MAX }) : undefined}
            data-testid="message-length">
            {input.trim().length} / {MESSAGE_MAX}
          </span>
        )}
        <button type="button" onClick={() => handleSend()} disabled={!canSend}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label={t('bots.chat.send')}>
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
        </span>
      )}
      </div>
    </div>
  );

  const lastItem = [...timeline].reverse().find((i) => i.type !== 'notes');  // what Retry answers again
  const openPanelTab = (key) => {
    storedTab.set(key);
    setTabRequest({ key, n: Date.now() });
    if (wide) setSide('project'); else setDetailsOpen(true);
  };
  openTabRef.current = openPanelTab;
  const teach = () => {
    setInput((v) => (v.trim().toLowerCase().startsWith('/learn') ? v : `/learn ${v}`));
    if (!wide) setDetailsOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  const retry = () => retryChat.mutate(chatId, {
    onError: (err) => { if (err?.response?.status !== 402) toast.error(t('bots.chat.send_failed')); },
  });
  const retryButton = (
    <button type="button" onClick={retry} disabled={retryChat.isPending || busy} title={t('bots.chat.retry')}
      data-testid="bot-retry" className="inline-flex items-center gap-1 p-1 rounded hover:bg-accent hover:text-foreground disabled:opacity-40">
      <RotateCcw className="w-3.5 h-3.5" />
    </button>
  );

  // A file the bot wrote on the computer, straight from the step list.
  const downloadFile = async (path) => {
    try {
      const res = await botsApi.workspaceDownload(placeId, path);
      await saveFile(res.data, path.split('/').pop());
    } catch {
      toast.error(t('bots.desktop.download_failed'));
    }
  };

  const openComputer = () => {
    if (wide) { setSide('computer'); return; }
    setNarrowComputer(true);
    setComputerCollapsed(false);
  };
  // The computer's screen shows next to or above the chat: the bot's cards then leave it out.
  const computerDocked = computerOn && (wide ? side === 'computer' : computerShown && !computerCollapsed);
  // The cards a run showed (decided ones fold to one line), under its steps.
  const runCards = (runId, pending) => {
    const cards = chatApprovals.filter((a) => a.run_id === runId && (pending ? a.status === 'pending' : a.status !== 'pending'));
    if (!cards.length) return null;
    return (
      <div className="space-y-2" data-testid={pending ? 'run-cards-pending' : 'run-cards'}>
        {cards.map((a) => (
          <ApprovalCard key={a.id} approval={a} botName={label} onOpenTask={openTaskDetail} onOpenComputer={openComputer}
            computerDocked={computerDocked} />
        ))}
      </div>
    );
  };

  const saveAsDoc = (messageId) => saveDoc.mutate(messageId, {
    onSuccess: () => toast.success(t('bots.chat.saved_doc')),
    onError: () => toast.error(t('bots.chat.send_failed')),
  });
  const costLabel = (run) => t('bots.chat.cost', {
    credits: (run.millicredits / 1000).toLocaleString(i18n.language, { maximumFractionDigits: run.millicredits < 1000 ? 2 : 1 }),
  });

  const renderItem = (item) => {
    if (item.type === 'notes') {
      const first = item.notes[0];
      return (
        <LearnedNotes key={`notes-${first.type === 'memory' ? first.e.id : first.l.id}`} notes={item.notes} t={t}
          onUndo={(id) => undoMemory.mutate(id)} onOpen={() => openPanelTab('learning')} />
      );
    }
    if (item.type === 'reply') {  // a run from before stored replies, still open or failed
      return (
        <BotTurn key={item.r.id}>
          <BotReply run={item.r} live={live[item.r.id]} bubble bot={bot} onOpenTask={openTaskDetail} onDownload={downloadFile}
            cards={runCards(item.r.id, false)} pendingCards={runCards(item.r.id, true)}
            onRetry={retry} retrying={retryChat.isPending} canRetry={item === lastItem} />
        </BotTurn>
      );
    }
    const m = item.m;
    if (m.event === 'handoff_result') {  // a handed conversation's first answer, back in the main chat (agents/queen.py)
      const back = m.handoff_result || {};
      const handedBot = { id: back.bot_id, name: back.bot_name, avatar_color: back.bot_color };
      return (
        <BotTurn key={m.id}>
          <div className="mt-2 flex items-center gap-3 rounded-2xl border border-border bg-card px-3 py-2.5 !items-start" data-testid="handoff-result">
            <BotAvatar bot={handedBot} size={24} state={back.status === 'failed' ? 'failed' : null} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {t(back.status === 'failed' ? 'bots.chat.handoff_failed'
                  : back.status === 'waiting' ? 'bots.chat.handoff_waiting' : 'bots.chat.handoff_answered', { bot: back.bot_name })}
              </p>
              {/* The answer that woke this bot is told in its own reply below (agents/queen.py _wake). */}
              {m.content && !m.woke && !m.wake_pending && <p className="mt-0.5 text-sm text-muted-foreground line-clamp-3">{m.content}</p>}
            </div>
            {!(mine && !(mine.bots || []).some((x) => x.id === back.bot_id)) && (
              <Button size="sm" variant="outline" asChild>
                <Link to={chatPath(back.chat_id)}>
                  {t('bots.chat.open_chat')}
                </Link>
              </Button>
            )}
          </div>
        </BotTurn>
      );
    }
    if (m.role === 'assistant') {
      const run = runsById[m.run_id];
      const status = run?.status || m.status || 'done';
      // The actions under an answer show on hover, in the gap below it, so a finished answer is
      // as far from the next message as any other. They take their own row on phones (always
      // shown) and with a note open under them.
      const inFlow = reportFor === m.id || (noteFor === m.id && m.feedback === 'down');
      return (
        <BotTurn key={m.id}>
          <div className="relative">
            <BotReply message={m} run={run} live={live[m.run_id]} bubble bot={bot} onOpenTask={openTaskDetail} onDownload={downloadFile}
              cards={m.run_id ? runCards(m.run_id, false) : null} pendingCards={m.run_id ? runCards(m.run_id, true) : null}
              onRetry={retry} retrying={retryChat.isPending} canRetry={item === lastItem} />
            {/* The bot found the project this chat is about (suggest_project): one click moves it there. */}
            {m.suggested_project && !projectId && status === 'done' && (
              <div className="mt-2 flex items-center gap-3 rounded-2xl border border-border bg-card px-3 py-2.5" data-testid="suggested-project">
                <ProjectTile project={m.suggested_project} size={24} />
                <span className="flex-1 min-w-0 text-sm text-foreground truncate">{m.suggested_project.name}</span>
                <Button size="sm" disabled={moveChat.isPending || busy} onClick={() => moveToProject(m.suggested_project.id)}
                  data-testid="suggested-project-move">
                  {t('bots.chat.move_to', { name: m.suggested_project.name })}
                </Button>
              </div>
            )}
            {/* The answer made bots (create_bot): open one, or undo it, as OpenMausBot's Undo card. */}
            {(m.made_bots || []).map((b) => {
              const gone = !!mine && !(mine.bots || []).some((x) => x.id === b.bot_id);
              return (
                <div key={b.bot_id} className="mt-2 flex items-center gap-3 rounded-2xl border border-border bg-card px-3 py-2.5" data-testid="made-bot-card">
                  <BotAvatar bot={{ id: b.bot_id, name: b.name, avatar_color: b.avatar_color }} size={24} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${gone ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                      {t('bots.chat.made_bot', { bot: b.name })}
                    </p>
                    {b.description && <p className="text-xs text-muted-foreground truncate">{b.description}</p>}
                  </div>
                  {gone ? (
                    <span className="text-xs text-muted-foreground">{t('bots.chat.made_bot_undone')}</span>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" disabled={deleteBot.isPending} data-testid="made-bot-undo"
                        onClick={() => deleteBot.mutate(b.bot_id, { onError: () => toast.error(t('bots.cards.failed')) })}>
                        {t('bots.chat.made_bot_undo')}
                      </Button>
                      <Button size="sm" variant="outline" asChild>
                        <Link to={newChatPath({ botId: b.bot_id })} data-testid="made-bot-open">{t('bots.hub.chat')}</Link>
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
            {/* BusyBee's main chat handed the conversation to a bot (hand_off_chat), a bot asked another
                (ask_bot) or opened a chat of its own (open_chat, it names the chat): open that chat. */}
            {(m.handoffs || []).map((h) => (
              <div key={h.chat_id} className="mt-2 flex items-center gap-3 rounded-2xl border border-border bg-card px-3 py-2.5" data-testid="handoff-card">
                <BotAvatar bot={{ id: h.bot_id, name: h.bot_name, avatar_color: h.bot_color }} size={24} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">
                    {h.mode === 'open' ? h.title : t(h.mode === 'ask' ? 'bots.chat.asked_bot' : 'bots.chat.handed_to', { bot: h.bot_name })}
                    {h.project && <span className="text-muted-foreground"> · {h.project.name}</span>}
                  </p>
                  {h.reason && <p className="text-xs text-muted-foreground truncate">{h.reason}</p>}
                </div>
                {/* Not for a bot undone since (made_bots above): its chat is gone with it. */}
                {!(mine && !(mine.bots || []).some((x) => x.id === h.bot_id)) && (
                  <Button size="sm" variant="outline" asChild>
                    <Link to={chatPath(h.chat_id)} data-testid="handoff-open">
                      {t('bots.chat.open_chat')}
                    </Link>
                  </Button>
                )}
              </div>
            ))}
            {status === 'done' && m.content && (
            <div data-testid="answer-actions"
              className={`mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground/70 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity max-md:opacity-100 max-md:-ml-2 max-md:gap-0 max-md:[&>button]:p-2 ${inFlow ? '' : 'md:absolute md:left-0 md:top-full md:mt-0 md:pt-0.5'}`}>
              <CopyButton text={m.content} t={t} />
              {['up', 'down'].map((v) => {
                const Icon = v === 'up' ? ThumbsUp : ThumbsDown;
                const on = m.feedback === v;
                return (
                  <button key={v} type="button" title={t(`bots.chat.rate_${v}`)} data-testid={`answer-rate-${v}`}
                    onClick={() => {
                      rate.mutate({ chatId, messageId: m.id, value: on ? null : v });
                      setNoteFor(v === 'down' && !on ? m.id : null);
                    }}
                    className={`p-1 rounded hover:bg-accent hover:text-foreground ${on ? (v === 'up' ? 'text-emerald-500' : 'text-red-500') : ''}`}>
                    <Icon className={`w-3.5 h-3.5 ${on ? 'fill-current' : ''}`} />
                  </button>
                );
              })}
              {!isMobile && (
                <button type="button" title={t('bots.report.action')} data-testid="answer-report"
                  onClick={() => { setReportFor(reportFor === m.id ? null : m.id); setNoteFor(null); }}
                  className={`p-1 rounded hover:bg-accent hover:text-foreground ${reportFor === m.id ? 'text-red-500' : ''}`}>
                  <Flag className="w-3.5 h-3.5" />
                </button>
              )}
              <SpeakButton text={m.content} />
              {isMobile ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" aria-label={t('common.more')} className="p-1 rounded hover:bg-accent hover:text-foreground" data-testid="answer-more">
                      <MoreHorizontal className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[200px]"
                    onCloseAutoFocus={(e) => {  // the document opens once this menu has closed (goAfterMenu)
                      if (goAfterMenu.current) { e.preventDefault(); const to = goAfterMenu.current; goAfterMenu.current = null; navigate(to); }
                    }}>
                    {m.document_id ? (
                      <DropdownMenuItem onSelect={() => { goAfterMenu.current = `/app/documents/${m.document_id}`; }}>
                        <FileText className="w-4 h-4 text-amber-500" />{t('bots.chat.open_doc')}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem disabled={saveDoc.isPending} onSelect={() => saveAsDoc(m.id)}>
                        <FilePlus2 className="w-4 h-4" />{t('bots.chat.save_doc')}
                      </DropdownMenuItem>
                    )}
                    {item === lastItem && !busy && (
                      <DropdownMenuItem disabled={retryChat.isPending} onSelect={retry}>
                        <RotateCcw className="w-4 h-4" />{t('bots.chat.retry')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onSelect={() => { setReportFor(m.id); setNoteFor(null); }}>
                      <Flag className="w-4 h-4" />{t('bots.report.action')}
                    </DropdownMenuItem>
                    {(m.model_id || run?.millicredits > 0) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                          {[m.model_id && (modelLabel(models, m.model_id) || m.model_id), run?.millicredits > 0 && costLabel(run)].filter(Boolean).join(' · ')}
                        </DropdownMenuLabel>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : m.document_id ? (
                <button type="button" onClick={() => navigate(`/app/documents/${m.document_id}`)} title={t('bots.chat.open_doc')}
                  className="p-1 rounded hover:bg-accent hover:text-foreground" data-testid="answer-open-doc">
                  <FileText className="w-3.5 h-3.5 text-amber-500" />
                </button>
              ) : (
                <button type="button" disabled={saveDoc.isPending} title={t('bots.chat.save_doc')} data-testid="answer-save-doc"
                  onClick={() => saveAsDoc(m.id)}
                  className="p-1 rounded hover:bg-accent hover:text-foreground disabled:opacity-40">
                  <FilePlus2 className="w-3.5 h-3.5" />
                </button>
              )}
              {!isMobile && item === lastItem && !busy && retryButton}
              {!isMobile && <span className="ml-1 tabular-nums" data-testid="reply-time">{formatTime(run?.finished_at || m.created_at)}</span>}
              {!isMobile && m.model_id && <span data-testid="reply-model">· {modelLabel(models, m.model_id) || m.model_id}</span>}
              {!isMobile && run?.millicredits > 0 && (
                <span title={t('bots.chat.cost_hint')}>· {costLabel(run)}</span>
              )}
            </div>
            )}
          </div>
          {reportFor === m.id && (
            <FeedbackNote t={t} busy={report.isPending} onClose={() => setReportFor(null)}
              placeholder={t('bots.report.placeholder')} sendLabel={t('bots.report.send')}
              onSend={(note) => report.mutate({ messageId: m.id, note }, {
                onSuccess: () => { setReportFor(null); toast.success(t('bots.report.thanks')); },
                onError: () => toast.error(t('bots.report.failed')),
              })} />
          )}
          {noteFor === m.id && m.feedback === 'down' && (
            <FeedbackNote t={t} busy={rate.isPending} onClose={() => setNoteFor(null)}
              onSend={(note) => rate.mutate({ chatId, messageId: m.id, value: 'down', note }, {
                onSuccess: (res) => {
                  setNoteFor(null);
                  toast.success(t(res?.learning ? 'bots.feedback.learning' : 'bots.feedback.thanks'));
                },
              })} />
          )}
        </BotTurn>
      );
    }
    if (m.event === 'handoff') {  // BusyBee's main chat started this conversation with its brief (agents/queen.py)
      const brief = (m.content || '').replace(/^\[[^\]]*\]\n?/, '');
      return (
        <div key={m.id} className="flex flex-col items-end gap-2" data-testid="handoff-note">
          <span className="self-center text-[11px] text-muted-foreground">
            {/* A bot that asked for this work (ask_bot) is not the main chat handing a conversation over. */}
            {t(/^\[A request from /.test(m.content || '') ? 'bots.chat.asked_by' : 'bots.chat.handed_over',
              { bot: m.from_bot?.name || 'BusyBee' })}
          </span>
          <div className="max-w-[min(85%,560px)] bg-bubble-user text-bubble-user-fg rounded-[18px] rounded-br-md px-4 py-2.5">
            <p className="whitespace-pre-wrap text-sm max-md:text-[15px] leading-relaxed">{brief}</p>
          </div>
        </div>
      );
    }
    if (m.event === 'moved') {  // the chat moved into a project (backend move_bot_chat)
      return (
        <div key={m.id} className="flex justify-center" data-testid="chat-moved-note">
          <span className="text-[11px] text-muted-foreground">
            {t('bots.chat.moved', { name: project?.name || '' })}
          </span>
        </div>
      );
    }
    return (
      <div key={m.id} className="flex flex-col items-end gap-2">
        <MessageFiles files={m.files} />
        {m.content && (
          <div className="max-w-[min(85%,560px)] bg-bubble-user text-bubble-user-fg rounded-[18px] rounded-br-md px-4 py-2.5">
            <p className="whitespace-pre-wrap text-sm max-md:text-[15px] leading-relaxed">{m.content}</p>
          </div>
        )}
        {/* Written while the bot worked and not read yet (backend agents/steering.py): it takes it at its
            next step or answers it right after; until then it can be taken back. */}
        {m.steer && !m.steer.claimed_at && (
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="message-waiting">
            <Clock className="w-3 h-3" />{t('bots.chat.waiting_for_bot', { name })}
            <button type="button" className="ml-1 hover:text-foreground underline-offset-2 hover:underline"
              onClick={() => cancelWaiting.mutate({ messageId: m.id, chatId })} disabled={cancelWaiting.isPending}
              data-testid="message-waiting-cancel">
              {t('bots.chat.waiting_cancel')}
            </button>
          </span>
        )}
      </div>
    );
  };

  // The chat's header in the Chats page, the same on computers and phones (there with the arrow
  // back to the history): the bot, the chat's title and where it is ("Bot · Project"; a chat in
  // no project moves into one from there), a Computer chip while the bot's computer runs, and
  // everything else in one "..." menu. The bot is changed in the composer only.
  const myBot = (mine?.bots || []).find((b) => b.id === botId) || (mine?.queen?.id === botId ? mine.queen : null);
  const canMove = !isQueen && !project && projects.length > 0 && !openChat?.task_id && (!chatId || ownChat);
  const computerOpen = wide ? side === 'computer' : computerShown;
  const toggleComputer = () => (wide ? setSide(side === 'computer' ? 'none' : 'computer') : toggleNarrowComputer());
  const waitingDetails = badges.tasks + badges.learning;
  const headerIcon = 'w-9 h-9 shrink-0 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent';
  const place = project ? (
    <Link to={`/app/projects?project=${project.id}`} data-testid="chat-header-project"
      className="flex items-center gap-1.5 min-w-0 hover:text-foreground transition-colors">
      <ProjectDot project={project} /><span className="truncate">{project.name}</span>
    </Link>
  ) : canMove ? (
    <Popover open={projectOpen} onOpenChange={setProjectOpen}>
      <PopoverAnchor asChild>
        <button type="button" onClick={() => setProjectOpen(true)} data-testid="chat-header-move"
          title={chatId ? t('bots.chat.move_to_project') : t('bots.chat.switch_project')}
          className="inline-flex items-center gap-1 min-w-0 hover:text-foreground transition-colors">
          <span className="truncate">{t('common.no_project')}</span><ChevronDown className="w-3.5 h-3.5 shrink-0" />
        </button>
      </PopoverAnchor>
      <PopoverContent align="start" className="w-80 p-0">
        {chatId && <p className="px-3 pt-3 text-xs text-muted-foreground">{t('bots.chat.move_hint')}</p>}
        <ProjectList compact autoFocus projects={projects} projectId={null}
          onPick={(id) => { setProjectOpen(false); if (!id) return; if (chatId) moveToProject(id); else selectProject(id); }} />
      </PopoverContent>
    </Popover>
  ) : <span className="truncate">{t('common.no_project')}</span>;
  const chatHeader = (
    <div className="flex items-center gap-2.5 flex-shrink-0 h-[calc(3.5rem+env(safe-area-inset-top))] md:h-[60px] pl-1 pr-2 md:pl-5 md:pr-4 border-b border-border"
      style={{ paddingTop: 'env(safe-area-inset-top)' }} data-testid="chat-header">
      {onBack && (
        <button type="button" onClick={onBack} className={`${headerIcon} text-foreground -mr-1`} aria-label={t('common.back')}
          title={t('common.back')} data-testid="chat-back">
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      <BotAvatar bot={bot} size={isMobile ? 34 : 30} state={busy ? 'working' : botState(myBot)} />
      <div className="flex-1 min-w-0">
        {renaming !== null ? (
          <input autoFocus value={renaming} maxLength={120} onChange={(e) => setRenaming(e.target.value)} onBlur={saveTitle}
            onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setRenaming(null); }}
            aria-label={t('bots.chat.rename')} data-testid="chat-rename-input"
            className="w-full text-sm rounded-md border border-amber-500/60 bg-background px-2 py-1 focus:outline-none" />
        ) : (
          <h1 className="text-[15px] md:text-[14.5px] font-semibold leading-tight text-foreground truncate"
            onDoubleClick={() => { if (ownChat) setRenaming(current?.chat?.title || ''); }} data-testid="chat-title">
            {openChat?.task_id && <SquareCheckBig className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5 text-muted-foreground" />}
            {current?.chat?.title || t('bots.chat.new_chat')}
          </h1>
        )}
        <div className="mt-0.5 flex items-center gap-1.5 min-w-0 text-[12.5px] text-muted-foreground" data-testid="chat-header-meta">
          <BotName bot={myBot || bot} className="min-w-0 max-w-[55%] shrink-0" />
          <span aria-hidden="true">·</span>
          {place}
        </div>
      </div>
      {computerOn && hasComputer && (wide || workspace?.available) && (
        <button type="button" onClick={toggleComputer} aria-pressed={computerOpen} data-testid="bot-computer-toggle"
          title={t('bots.desktop.computer_title')}
          className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full border border-border text-xs text-foreground hover:bg-accent shrink-0 transition-colors ${computerOpen ? 'bg-accent' : ''}`}>
          <span className="relative">
            <Monitor className="w-4 h-4" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-background" />
          </span>
          <span className="max-sm:hidden">{t('bots.desktop.computer_title')}</span>
        </button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={`${headerIcon} relative`} aria-label={t('common.more')} title={t('common.more')}
            data-testid="chat-menu">
            <MoreHorizontal className="w-5 h-5" />
            {waitingDetails > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-amber-500" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[220px]"
          onCloseAutoFocus={(e) => {
            if (moveAfterMenu.current) { e.preventDefault(); moveAfterMenu.current = false; setProjectOpen(true); }
            if (goAfterMenu.current) { e.preventDefault(); const to = goAfterMenu.current; goAfterMenu.current = null; navigate(to); }
          }}>
          {hasComputer && (wide || workspace?.available) && (
            <DropdownMenuItem onSelect={toggleComputer} data-testid="chat-menu-computer">
              <Monitor className="w-4 h-4" />{t('bots.desktop.computer_title')}
              {computerOn && <span className="ml-auto w-2 h-2 rounded-full bg-emerald-500" />}
            </DropdownMenuItem>
          )}
          {hasComputer && workspace?.available && (
            <DropdownMenuItem onSelect={() => setFilesOpen(true)} data-testid="chat-menu-files">
              <FolderOpen className="w-4 h-4" />{t('bots.desktop.files')}
            </DropdownMenuItem>
          )}
          {projectId && (
            <DropdownMenuItem onSelect={toggleProjectPanel} data-testid="bot-details">
              <PanelRight className="w-4 h-4" />{t('bots.chat.details')}
              {waitingDetails > 0 && (
                <span className="ml-auto min-w-[15px] h-[15px] rounded-full bg-attention-soft text-orange-700 dark:text-attention-text text-[10px] font-semibold leading-[15px] px-1 text-center">
                  {waitingDetails}
                </span>
              )}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => navigate('/app/bots/scheduled')} data-testid="chat-menu-scheduled">
            <CalendarClock className="w-4 h-4" />{t('bots.scheduled.title')}
          </DropdownMenuItem>
          {isQueen && (  // BusyBee's own memory and the person's skills (Settings > Bots)
            <DropdownMenuItem onSelect={() => { goAfterMenu.current = '/app/settings?tab=bots'; }} data-testid="chat-menu-memory">
              <Brain className="w-4 h-4" />{t('bots.queen_memory')}
            </DropdownMenuItem>
          )}
          {(ownChat || (scope?.can_edit && !isQueen)) && <DropdownMenuSeparator />}
          {ownChat && (
            <DropdownMenuItem onSelect={() => setRenaming(current?.chat?.title || '')} data-testid="chat-menu-rename">
              <Pencil className="w-4 h-4" />{t('bots.chat.rename')}
            </DropdownMenuItem>
          )}
          {ownChat && canMove && (
            <DropdownMenuItem onSelect={() => { moveAfterMenu.current = true; }} data-testid="chat-menu-move">
              <FolderInput className="w-4 h-4" />{t('bots.chat.move_to_project')}
            </DropdownMenuItem>
          )}
          {scope?.can_edit && !isQueen && (
            <DropdownMenuItem onSelect={() => setEditing(true)} data-testid="chat-menu-edit-bot">
              <Pencil className="w-4 h-4" />{t('bots.edit_bot')}
            </DropdownMenuItem>
          )}
          {ownChat && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setConfirmDelete(true)} data-testid="chat-menu-delete"
                className="text-red-500 focus:text-red-500">
                <Trash2 className="w-4 h-4" />{t('bots.chat.delete_chat')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const rightPanel = wide && side === 'computer' && hasComputer ? (
    <aside className="flex w-[44%] max-w-[760px] min-w-[380px] shrink-0 flex-col border-l border-border" data-testid="bot-side-computer">
      {workspace?.available ? (
        <ComputerView projectId={placeId} bot={bot} botName={name} live={live} activity={activity} layout="panel" onClose={() => setSide('none')} />
      ) : (
        <div className="p-4 space-y-3">
          <div className="flex justify-end">
            <button type="button" onClick={() => setSide('none')} title={t('common.close')}
              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"><X className="w-3.5 h-3.5" /></button>
          </div>
          <DesktopUpsell projectId={placeId} />
        </div>
      )}
    </aside>
  ) : wide && side === 'project' && projectId ? (  // a chat in no project has no project details
    <aside className="flex w-[23.5rem] shrink-0 flex-col border-l border-border">
      <ProjectPanel t={t} badges={badges} tabRequest={tabRequest} onClose={() => setSide('none')}>{panelContent}</ProjectPanel>
    </aside>
  ) : null;

  return (
    <div className="h-full min-h-0 flex flex-col">
      {embedded ? embedToolbar?.({
        title: current?.chat?.title || null,
        computerButton: hasComputer && workspace?.available ? (
          <button type="button" className={`${iconButton} ${computerShown ? 'bg-accent text-foreground' : ''}`}
            onClick={toggleNarrowComputer} title={t('bots.desktop.computer_title')} aria-label={t('bots.desktop.computer_title')}
            data-testid="bot-computer-toggle">
            <span className="relative">
              <Monitor className="w-4 h-4" />
              {computerOn && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-background" />}
            </span>
          </button>
        ) : null,
        detailsButton: projectId ? (
          <button type="button" className={`${iconButton} relative`} onClick={toggleProjectPanel}
            aria-label={t('bots.chat.details')} title={t('bots.chat.details')} data-testid="bot-details">
            <PanelRight className="w-4 h-4" />
            {badges.tasks + badges.learning > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />}
          </button>
        ) : null,
      }) : chatHeader}

      {/* A bot can be talked to without any project (a chat in no project). */}
      {(
        <div className="flex-1 flex min-h-0">
          <div className="relative flex-1 flex flex-col min-w-0"
            onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragging(true); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
            onDrop={(e) => { if (e.dataTransfer?.files?.length) { e.preventDefault(); attachments.add(e.dataTransfer.files); } setDragging(false); }}>
            {dragging && (
              <div className="pointer-events-none absolute inset-3 z-20 rounded-3xl border-2 border-dashed border-amber-500 bg-background/80 flex items-center justify-center text-sm font-medium text-foreground">
                {t('bots.files.drop')}
              </div>
            )}
            {paused && (
              <div className="flex items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-700 dark:text-amber-300" data-testid="bots-paused-banner">
                <Pause className="w-4 h-4 shrink-0" />
                <span className="flex-1">{t(`bots.paused.${paused}`)}</span>
                {paused === 'project' && (
                  <Button size="sm" variant="outline" className="h-7" disabled={setPaused.isPending}
                    onClick={() => setPaused.mutate({ projectId, paused: false }, { onError: () => toast.error(t('bots.paused.no_permission')) })}>
                    {t('bots.paused.turn_on')}
                  </Button>
                )}
                {paused === 'budget' && (
                  <Button size="sm" variant="outline" className="h-7" asChild>
                    <Link to={`/app/settings?tab=bots&team=${project?.team_id || 'personal'}`}>{t('bots.budget.change')}</Link>
                  </Button>
                )}
              </div>
            )}
            {!wide && computerShown && (
              <ComputerView projectId={placeId} bot={bot} botName={name} live={live} activity={activity}
                collapsed={computerCollapsed} onCollapse={setComputerCollapsed} />
            )}
            {timeline.length === 0 && isMobile ? (
              <div className="flex-1 min-h-0 flex flex-col" data-testid="bot-conversation">
                <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center px-8 text-center">
                  <BotAvatar bot={bot} size={60} className="mb-4" />
                  <h2 className="text-[26px] leading-tight font-semibold tracking-tight text-foreground">{t('bots.chat.hello', { name })}</h2>
                  {project?.name && <p className="mt-2 text-[15px] text-muted-foreground">{t('bots.chat.intro_mobile', { project: project.name })}</p>}
                </div>
                <div className="flex gap-2 overflow-x-auto bb-no-scrollbar px-3 pb-2.5" data-testid="bot-suggestions">
                  {SUGGESTIONS.map((key) => (
                    <button key={key} type="button" onClick={() => handleSend(t(`bots.chat.suggestions.${key}`))}
                      className="shrink-0 w-40 text-left rounded-2xl border border-border bg-card px-3.5 py-3 text-sm leading-snug text-foreground active:bg-accent">
                      <span className="line-clamp-2">{t(`bots.chat.suggestions.${key}`)}</span>
                    </button>
                  ))}
                </div>
                <div className={`px-3 ${embedded ? 'pb-3' : 'pb-[calc(0.75rem+env(safe-area-inset-bottom))]'}`}>{composer}</div>
              </div>
            ) : timeline.length === 0 ? (
              <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center px-4 sm:px-6 py-8" data-testid="bot-conversation">
                <div className="w-full max-w-2xl text-center">
                  {/* The bee and the greeting get room above and below, as on phones (above). */}
                  <div className="mb-8">
                    <BotAvatar bot={bot} size={56} className="mx-auto mb-5" />
                    <h2 className="text-2xl leading-tight font-semibold tracking-tight text-foreground">{t('bots.chat.hello', { name })}</h2>
                    {/* In no project there is no project to name (as on phones, above). */}
                    {project?.name && <p className="mt-2 text-sm text-muted-foreground">{t('bots.chat.intro_project', { project: project.name })}</p>}
                  </div>
                  <div className="text-left">{composer}</div>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {SUGGESTIONS.map((key) => (
                      <button key={key} type="button" onClick={() => handleSend(t(`bots.chat.suggestions.${key}`))}
                        className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted/40 hover:bg-muted/60 border border-border rounded-full transition-colors">
                        {t(`bots.chat.suggestions.${key}`)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="bot-conversation">
                  <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 py-6 space-y-6 max-md:py-5 max-md:space-y-5">
                    {firstDrawn > 0 && <div ref={topRef} aria-hidden="true" data-testid="chat-older" />}
                    {timeline.slice(firstDrawn).map((item, j) => {
                      const i = firstDrawn + j;
                      const at = itemAt(item);
                      const prevAt = timeline.slice(0, i).reverse().map(itemAt).find(Boolean);
                      const gap = at && (!prevAt || new Date(at) - new Date(prevAt) >= TIME_GAP_MS
                        || formatDate(at) !== formatDate(prevAt));
                      if (!gap) return renderItem(item);
                      return (
                        <div key={`at-${at}-${i}`} className="space-y-4 max-md:space-y-3">
                          <div className="pt-1 text-center text-xs text-muted-foreground/70" data-testid="chat-time">
                            {formatDate(at, true)} {formatTime(at)}
                          </div>
                          {renderItem(item)}
                        </div>
                      );
                    })}
                    <div ref={endRef} />
                  </div>
                </div>
                <div className={`px-4 sm:px-6 pb-4 pt-2 max-md:px-3 max-md:pt-1 ${embedded ? 'max-md:pb-3' : 'max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom))]'}`}>
                  <div className="max-w-3xl mx-auto">
                    {composer}
                    {!isMobile && <p className="mt-1.5 text-[10px] text-muted-foreground/60 text-center">{t('bots.chat.input_hint')}</p>}
                  </div>
                </div>
              </>
            )}
          </div>

          {rightPanel}
          <Sheet open={detailsOpen && !wide} onOpenChange={setDetailsOpen}>
            <SheetContent side="right" className="w-[88vw] sm:max-w-sm p-0 flex flex-col">
              <SheetTitle className="sr-only">{t('bots.chat.details')}</SheetTitle>
              <SheetDescription className="sr-only">{label}</SheetDescription>
              <div className="flex-1 min-h-0 pt-8">
                <ProjectPanel t={t} badges={badges} tabRequest={tabRequest}>{panelContent}</ProjectPanel>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      )}

      <BotFormModal open={editing} onClose={() => setEditing(false)} bot={bot} teamId={teamId}
        models={models} inheritLabel={botInheritLabel} onDeleted={() => navigate('/app/bots', { replace: true })} />
      {filesOpen && <WorkspaceFiles projectId={placeId} open onClose={() => setFilesOpen(false)} />}
      <ConfirmModal isOpen={confirmDelete} onClose={() => setConfirmDelete(false)} danger
        title={t('bots.chat.delete_chat')} message={current?.chat?.title || t('bots.chat.new_chat')} confirmLabel={t('common.delete')}
        onConfirm={() => { setConfirmDelete(false); handleDelete(chatId); }} />
    </div>
  );
};

export default BotPage;
